import { sbEditable } from '@storyblok/storyblok-editable';
import gsap from 'gsap';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import styles from './BornAndRaised.module.scss';
import type { BornAndRaisedProps, BornAndRaisedRef } from './BornAndRaised.data';
import { TransitionPresence } from '../../../util/react-transition-component';
import { setupTransitionInTimeline } from './BornAndRaised.transitions';
import TextSlide from './text-slide/TextSlide';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useWindowSize from '../../../hooks/useWindowSize';
// import LottieView from '../../atoms/lottie-view/LottieView';
import type { LottieViewRef } from '../../atoms/lottie-view/LottieView.data';

export default function BornAndRaised({
  ref,
  ...props
}: BornAndRaisedProps & { ref?: Ref<BornAndRaisedRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const keyPrefix = props.blok._uid;

  const contentFrameRef = useRef<HTMLDivElement>(null);
  const circle1Ref = useRef<HTMLDivElement>(null);
  const circle2Ref = useRef<HTMLDivElement>(null);
  const circle1LottieRef = useRef<LottieViewRef>(null);
  const circle2LottieRef = useRef<LottieViewRef>(null);
  const [activeTextSlide, setActiveTextSlide] = useState(-1);
  const windowSize = useWindowSize();

  useEffect(() => {
    const size = Math.hypot(windowSize.width, windowSize.height);
    gsap.set([circle1Ref.current, circle2Ref.current], { width: size, height: size });
  }, [windowSize.width, windowSize.height]);

  useScrollTrigger({
    trigger: elementRef,
    animation: useSetupTimeline(setupTransitionInTimeline, {
      circle1Ref,
      circle2Ref,
      circle1LottieRef,
      circle2LottieRef,
    }),
    vars: {
      scrub: true,
      start: 'top center',
      end: 'bottom bottom-=50%', // From bottom to bottom-=50% so the transition with next section is less long
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: contentFrameRef,
    vars: {
      start: 'top top',
      end: 'bottom top',
      pinType: 'transform',
      anticipatePin: 1,
      scrub: true,
      onUpdate: ({ progress }) => {
        if (progress > 0 && progress < 0.4) {
          setActiveTextSlide(0);
        } else if (progress >= 0.4 && progress < 0.8) {
          setActiveTextSlide(1);
        } else {
          setActiveTextSlide(-1);
        }
      },
    },
  });

  const data = {
    section1: {
      topLeftTitle: props.blok.section1_top_left_title,
      bottomRightTitle: props.blok.section1_bottom_right_title,
      kanjiText: props.blok.section1_kanji_text,
    },
    section2: {
      topLeftTitle: props.blok.section2_top_left_title,
      bottomRightTitle: props.blok.section2_bottom_right_title,
      kanjiText: props.blok.section2_kanji_text,
    },
  };

  return (
    <div
      ref={elementRef}
      className={classNames(styles.bornAndRaised, props.className)}
      {...sbEditable(props.blok)}
    >
      <div ref={contentFrameRef} className={styles.contentFrame}>
        <div ref={circle1Ref} className={classNames(styles.circle)}>
          {/* <LottieView */}
          {/*  ref={circle1LottieRef} */}
          {/*  id="icon-rising-sun-open" */}
          {/*  loop={false} */}
          {/*  className={classNames(styles.lottieIcon, 'abs-center')} */}
          {/* /> */}
        </div>
        <div ref={circle2Ref} className={classNames(styles.circle, styles.circle2)}>
          {/* <LottieView */}
          {/*  ref={circle2LottieRef} */}
          {/*  id="icon-rising-sun-full-markers" */}
          {/*  loop={false} */}
          {/*  className={classNames(styles.lottieIcon, 'abs-center')} */}
          {/* /> */}
        </div>

        <TransitionPresence>
          {activeTextSlide >= 0 && (
            <TextSlide
              key={`${keyPrefix}_textSlide_${activeTextSlide}`}
              blok={activeTextSlide === 1 ? { ...data.section2 } : { ...data.section1 }}
              className={styles.textSlide}
              transitionOnMount
              transitionOnUnMount
            />
          )}
        </TransitionPresence>
      </div>
    </div>
  );
}
